import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatET } from "@/lib/api";
import MobileShell from "@/components/admin/MobileShell";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const TABS = [
  { id: "new_leads", label: "New Leads", filter: { kind: "client", status: "new_lead" } },
  { id: "upcoming", label: "Upcoming", filter: { kind: "appointment", status: "scheduled" } },
  { id: "completed", label: "Completed", filter: { kind: "appointment", status: "completed" } },
  { id: "contracts_pending", label: "Pending Contracts", filter: { kind: "contract", status: "sent" } },
  { id: "contracts_signed", label: "Signed", filter: { kind: "contract", status: "signed" } },
  { id: "contracts_expired", label: "Expired", filter: { kind: "contract", status: "expired" } },
];

export default function AdminDashboard() {
  const [active, setActive] = useState("new_leads");
  const [counts, setCounts] = useState({});
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [purging, setPurging] = useState(false);

  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setCounts(data)).catch(()=>{}); }, []);

  const tab = useMemo(() => TABS.find((t) => t.id === active), [active]);

  const load = () => {
    if (tab.filter.kind === "client") {
      api.get("/admin/clients", { params: { status_filter: tab.filter.status, q: q || undefined } }).then(({ data }) => setClients(data || []));
    } else if (tab.filter.kind === "appointment") {
      api.get("/admin/clients", { params: { q: q || undefined } }).then(({ data }) => {
        setClients((data || []).filter((c) => c.next_appointment && c.next_appointment.status === tab.filter.status));
      });
    } else if (tab.filter.kind === "contract") {
      api.get("/admin/contracts", { params: { status_filter: tab.filter.status } }).then(({ data }) => {
        const list = (data || []).filter((c) => !q || (c.client?.full_name || "").toLowerCase().includes(q.toLowerCase()) || (c.client?.email || "").toLowerCase().includes(q.toLowerCase()));
        setContracts(list);
      });
    }
  };

  // Reload list whenever tab or query changes; clear selection on tab switch.
  useEffect(() => { setSelected(new Set()); load(); /* eslint-disable-next-line */ }, [active, q]);

  const toggle = (clientId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const visibleIds = tab.filter.kind === "contract"
    ? contracts.map((c) => c.client_id).filter(Boolean)
    : clients.map((c) => c.client_id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const bulkPurge = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/clients/bulk-purge", { client_ids: ids });
      toast.success(`Deleted ${data.clients_deleted ?? ids.length} client${(data.clients_deleted ?? ids.length) === 1 ? "" : "s"} permanently.`);
      clearSelection();
      load();
      api.get("/admin/dashboard").then(({ data: d }) => setCounts(d)).catch(()=>{});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete clients.");
    } finally {
      setPurging(false);
    }
  };

  return (
    <MobileShell title="Inbox">
      <div className="mt-2 mb-3">
        <h1 className="font-display text-2xl text-[#0A1930] tracking-tight">Today's mission</h1>
        <p className="text-sm text-slate-500 mt-1">Manage Veterans, consultations, and contracts.</p>
      </div>

      <div className="relative" data-testid="admin-search">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, phone, branch…"
          className="pl-9 h-11 rounded-full border-slate-200 bg-white"
          data-testid="admin-search-input"
        />
      </div>

      {/* Status chips */}
      <div className="mt-4 -mx-4 px-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
        {TABS.map((t) => {
          const c = counts[t.id] ?? 0;
          const isActive = active === t.id;
          return (
            <button key={t.id} onClick={() => setActive(t.id)}
              data-testid={`tab-${t.id}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-all ${isActive ? "bg-[#0A1930] text-white border-[#0A1930]" : "bg-white text-[#0A1930] border-slate-200"}`}>
              <span>{t.label}</span>
              <span className={`ml-2 text-[11px] ${isActive ? "text-white/80" : "text-slate-400"}`}>{c}</span>
            </button>
          );
        })}
      </div>

      {/* Select-all row (only when list non-empty) */}
      {visibleIds.length > 0 && (
        <div className="mt-4 flex items-center justify-between px-1" data-testid="select-all-row">
          <label className="inline-flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAllVisible}
              className="h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
              data-testid="select-all-checkbox"
            />
            <span>Select all {visibleIds.length}</span>
          </label>
          {selected.size > 0 && (
            <button onClick={clearSelection} className="text-xs text-slate-500 underline-offset-2 hover:underline" data-testid="clear-selection-btn">
              Clear ({selected.size})
            </button>
          )}
        </div>
      )}

      {/* List */}
      <div className="mt-3 space-y-2 pb-24">
        {tab.filter.kind !== "contract" ? (
          clients.length === 0 ? <Empty label={`No items in ${tab.label}.`} /> :
          clients.map((c) => (
            <ClientRow
              key={c.client_id}
              c={c}
              checked={selected.has(c.client_id)}
              onToggle={() => toggle(c.client_id)}
            />
          ))
        ) : (
          contracts.length === 0 ? <Empty label={`No ${tab.label.toLowerCase()} contracts.`} /> :
          contracts.map((c) => (
            <ContractRow
              key={c.contract_id}
              c={c}
              checked={selected.has(c.client_id)}
              onToggle={() => c.client_id && toggle(c.client_id)}
            />
          ))
        )}
      </div>

      {/* Floating bulk-purge bar */}
      {selected.size > 0 && (
        <BulkPurgeBar
          count={selected.size}
          onConfirm={bulkPurge}
          purging={purging}
          onClear={clearSelection}
        />
      )}
    </MobileShell>
  );
}

function StatusDot({ status }) {
  const map = {
    new_lead: "bg-amber-500", consulted: "bg-emerald-500", contract_sent: "bg-blue-500",
    signed: "bg-emerald-600", not_interested: "bg-slate-400",
  };
  return <span className={`w-2 h-2 rounded-full ${map[status] || "bg-slate-300"}`} />;
}

function RowCheckbox({ checked, onToggle, testid }) {
  // Wrapper handles click without triggering parent navigation.
  return (
    <span
      className="shrink-0 flex items-center justify-center w-9 h-9 -ml-1"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
      data-testid={testid}
    >
      <input
        type="checkbox"
        checked={checked}
        readOnly
        className="h-5 w-5 rounded border-slate-300 text-rose-600 focus:ring-rose-500 pointer-events-none"
      />
    </span>
  );
}

function ClientRow({ c, checked, onToggle }) {
  return (
    <Link
      to={`/admin/clients/${c.client_id}`}
      className={`block bg-white border rounded-2xl p-3 pl-2 hover:border-slate-300 transition-all ${checked ? "border-rose-300 bg-rose-50/50" : "border-slate-200"}`}
      data-testid={`client-row-${c.client_id}`}
    >
      <div className="flex items-center gap-2">
        <RowCheckbox checked={checked} onToggle={onToggle} testid={`client-row-check-${c.client_id}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <StatusDot status={c.status} />
            <p className="font-medium text-[#0A1930] truncate">{c.full_name}</p>
          </div>
          <p className="text-xs text-slate-500 mt-1 truncate">{c.branch} · {c.email}</p>
          {c.next_appointment && (
            <p className="text-xs text-slate-500 mt-1">Consult: {formatET(c.next_appointment.date, c.next_appointment.time)}</p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
      </div>
    </Link>
  );
}

function ContractRow({ c, checked, onToggle }) {
  return (
    <Link
      to={`/admin/clients/${c.client_id}`}
      className={`block bg-white border rounded-2xl p-3 pl-2 hover:border-slate-300 transition-all ${checked ? "border-rose-300 bg-rose-50/50" : "border-slate-200"}`}
      data-testid={`contract-row-${c.contract_id}`}
    >
      <div className="flex items-center gap-2">
        <RowCheckbox checked={checked} onToggle={onToggle} testid={`contract-row-check-${c.contract_id}`} />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[#0A1930] truncate">{c.client?.full_name}</p>
          <p className="text-xs text-slate-500 mt-1 truncate">{c.client?.branch} · Fee {c.fee_percentage}% · {c.status}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
      </div>
    </Link>
  );
}

function Empty({ label }) {
  return (
    <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-center text-sm text-slate-500" data-testid="admin-empty">
      <AlertCircle className="w-5 h-5 mx-auto text-slate-300" />
      <p className="mt-2">{label}</p>
    </div>
  );
}

export function BulkPurgeBar({ count, onConfirm, purging, onClear }) {
  return (
    <div className="fixed bottom-[76px] left-0 right-0 z-30 px-4 pointer-events-none" data-testid="bulk-purge-bar">
      <div className="max-w-md mx-auto rounded-2xl bg-[#0A1930] text-white shadow-xl px-3 py-2.5 flex items-center gap-2 pointer-events-auto">
        <span className="text-sm font-medium pl-1">{count} selected</span>
        <button onClick={onClear} className="ml-auto text-xs text-white/70 hover:text-white px-2 py-1" data-testid="bulk-clear-btn">
          Clear
        </button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="inline-flex items-center gap-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 transition-all px-3 py-2 text-sm font-medium"
              data-testid="bulk-purge-btn"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Permanently delete {count} client{count === 1 ? "" : "s"}?</AlertDialogTitle>
              <AlertDialogDescription>
                Every selected client and ALL of their related records — appointments, notes, contracts, text logs, and email logs — will be wiped from the system. This <span className="font-semibold">cannot be undone</span>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="bulk-purge-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onConfirm}
                disabled={purging}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="bulk-purge-confirm"
              >
                {purging ? "Deleting…" : `Yes, delete ${count}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
